import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, FormControlLabel, FormGroup, Grid, Paper, Switch, Typography } from '@mui/material'
import { TextField } from '@carsys/form'
import { ColorPicker, FileUploader, UploadButton } from '@carsys/ui'
import { useFormikContext } from 'formik'
import { IThemeFormValues } from './ThemeForm'
import ThemePreview from './ThemePreview'
import { useMutateThemeLogo } from './useMutateThemeLogo'

const requiredStyling = { '&::after': { content: "'*'", color: 'red', ml: 0.5, fontWeight: 300 } }

const General: React.FC = () => {
  const { t } = useTranslation()
  const mutateThemeLogo = useMutateThemeLogo()
  const { values, setFieldValue } = useFormikContext<IThemeFormValues>()
  const [logoLarge, setLogoLarge] = React.useState<string>('')
  const [logoSmall, setlogoSmall] = React.useState<string>('')
  const [isActive, setIsActive] = React.useState<boolean>(values.IsActive)
  const [themeColor, setThemeColor] = React.useState<string>(values.MainColour)
  const [menuColor, setMenuColor] = React.useState<string>(values.MainColourText)
  const previewImageLarge = logoLarge || values.LogoURL
  const previewImageSmall = logoSmall || values.LogoSmallURL

  const handleLogoSet = (logoSize: string, files: File[]) => {
    if (logoSize === 'large' && files.length > 0) {
      setLogoLarge(URL.createObjectURL(files[0]))
    } else if (logoSize === 'small' && files.length > 0) {
      setlogoSmall(URL.createObjectURL(files[0]))
    } else {
      setLogoLarge('')
      setlogoSmall('')
    }
  }

  const handleFileUpload = async (fieldName: string, file: File) => {
    const { Data } = await mutateThemeLogo.mutateAsync(file)
    setFieldValue(fieldName, Data.URL)
  }

  const handleIsActive = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsActive(event.target.checked)
    setFieldValue('IsActive', event.target.checked)
  }

  const handleColor = async (fieldName: string, event: React.ChangeEvent<string>) => {
    const target = event.target as unknown
    fieldName === 'MainColour' ? setThemeColor((target as HTMLInputElement).value) : setMenuColor((target as HTMLInputElement).value)
    setFieldValue(fieldName, (target as HTMLInputElement).value)
  }

  React.useEffect(() => {
    return () => {
      if (logoLarge) {
        URL.revokeObjectURL(logoLarge)
      }
      if (logoSmall) {
        URL.revokeObjectURL(logoSmall)
      }
    }
  }, [logoLarge, logoSmall])

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant='h6' sx={{ mb: 1 }}>
              {t('theme.edit_styleSection_title')}
            </Typography>
            <FormGroup>
              <TextField data-testid='theme-name' label={t('theme.theme_name')} inputProps={{ maxLength: '100' }} name='Name' required />
            </FormGroup>
          </Grid>
          <Grid item xs={4}>
            <ColorPicker
              label={t('theme.theme_color')}
              name='MainColour'
              value={themeColor}
              onChange={event => handleColor('MainColour', event)}
            />
          </Grid>
          <Grid item xs={4}>
            <ColorPicker
              label={t('theme.theme_text_color')}
              name='MainColourText'
              value={menuColor}
              onChange={event => handleColor('MainColourText', event)}
            />
          </Grid>
          <Grid item xs={4}>
            <FormGroup>
              <FormControlLabel
                control={<Switch data-testid='theme-active' name='IsActive' checked={isActive} onChange={event => handleIsActive(event)} />}
                label={t('theme.theme_active')}
                sx={{ alignSelf: 'baseline' }}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={6} data-testid='theme-logo'>
            <Paper>
              <Box sx={{ p: 2, pb: 0 }}>
                <Typography variant='h6' sx={{ fontWeight: 700, ...requiredStyling }}>
                  {t('theme.upload_logo')}
                </Typography>
                {previewImageLarge && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, p: 2, border: '1px solid rgba(0,0,0,.2)' }}>
                    <img
                      src={previewImageLarge}
                      style={{ objectFit: 'contain', display: 'block', maxWidth: '100%', maxHeight: 75 }}
                      data-testid='logo-preview'
                      alt='logo-preview'
                    />
                  </Box>
                )}
              </Box>

              <FileUploader PaperComponent={Box} multiple={false} onChange={files => handleLogoSet('large', files)}>
                {({ files, setFiles }) => (
                  <Grid item xs={12} md={6}>
                    <UploadButton
                      loading={false}
                      disabled={files.length < 1}
                      onUpload={() => {
                        handleFileUpload('LogoURL', files[0])
                        setFiles([])
                      }}
                    />
                  </Grid>
                )}
              </FileUploader>
            </Paper>
          </Grid>
          <Grid item xs={6} data-testid='theme-logo'>
            <Paper>
              <Box sx={{ p: 2, pb: 0 }}>
                <Typography variant='h6' sx={{ fontWeight: 700, ...requiredStyling }}>
                  {t('theme.upload_logo_small')}
                </Typography>
                {previewImageSmall && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, p: 2, border: '1px solid rgba(0,0,0,.2)' }}>
                    <img
                      src={previewImageSmall}
                      style={{ objectFit: 'contain', display: 'block', maxWidth: '100%', maxHeight: 75 }}
                      data-testid='logo-preview'
                      alt='logo-preview'
                    />
                  </Box>
                )}
              </Box>
              <FileUploader PaperComponent={Box} multiple={false} onChange={files => handleLogoSet('small', files)}>
                {({ files, setFiles }) => (
                  <Grid item xs={12} md={6}>
                    <UploadButton
                      loading={false}
                      disabled={files.length < 1}
                      onUpload={() => {
                        handleFileUpload('LogoSmallURL', files[0])
                        setFiles([])
                      }}
                    />
                  </Grid>
                )}
              </FileUploader>
            </Paper>
          </Grid>
        </Grid>
        <Typography
          variant='caption'
          sx={{ display: 'block', fontStyle: 'italic', mt: 1, '&::before': { content: "'*'", color: 'red', mr: 0.5 } }}
        >
          {t('theme.caption_required')}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <ThemePreview
          MainColour={themeColor}
          MenuTextColour={menuColor}
          LogoLargeURL={previewImageLarge}
          LogoSmallURL={previewImageSmall}
        />
      </Grid>
    </Grid>
  )
}
General.displayName = 'General'

export default General
